import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import { logRelayMigrationDiagnostic } from './relayDiagnostics.ts';
import { resolveRelayEnabledPackages } from './relayEnabledPackageSets.ts';
import { getRelayOperationKey, type RelayOperationVariables } from './relayOperation.ts';

const LEGACY_CONFLUENCE_PACKAGE_PREFIXES = ['@confluence/', '@confluence-classic/'] as const;

let disableRelayOverride: boolean | undefined;
let relayInteropEnabledOverride: boolean | undefined;

export function __setRelayInteropEnabledOverrideForFixtures(value: boolean | undefined): void {
	relayInteropEnabledOverride = value;
}

export function __TEST_ONLY_resetDisableRelayOverride(): void {
	disableRelayOverride = undefined;
}

type RelayDecisionPhase =
	| 'preload'
	| 'component'
	| 'lazy'
	| 'imperative'
	| 'cache'
	| 'mutation'
	| 'fragment'
	| 'subscription'
	| 'memo'
	| 'unknown';

export type RelayEnablementOptions = {
	operationName?: string | null;
	operationKey?: string | null;
	variables?: RelayOperationVariables;
	phase?: RelayDecisionPhase;
};

function isRelayDisabledByQueryParam(): boolean {
	if (disableRelayOverride !== undefined) {
		return disableRelayOverride;
	}

	try {
		if (typeof window !== 'undefined' && window.location?.search) {
			const params = new URLSearchParams(window.location.search);
			const value = params.get('disableRelay');
			disableRelayOverride = value === '1' || value === 'true';
		} else {
			disableRelayOverride = false;
		}
	} catch {
		disableRelayOverride = false;
	}

	return disableRelayOverride;
}

function matchesRelayEnabledPackage(callerPackage: string, enabledPackage: string): boolean {
	if (callerPackage === enabledPackage) {
		return true;
	}

	return LEGACY_CONFLUENCE_PACKAGE_PREFIXES.some(
		(packagePrefix) =>
			callerPackage === `${packagePrefix}${enabledPackage}` ||
			enabledPackage === `${packagePrefix}${callerPackage}`,
	);
}

/**
 * Whether `callerPackage` is enrolled by a `confluence_relay_enabled_packages` value, applying the
 * same set expansion and `@confluence/` prefix matching that `isRelayEnabled` uses.
 *
 * Exported for Bifrost, which resolves the rollout configuration from the native edge SDK before the
 * Statsig client exists and must reach the same verdict this file would. Sharing the predicate is the
 * point — reimplementing the prefix matching there would drift.
 */
export function isPackageRelayEnabled(
	configuredPackages: ReadonlyArray<string>,
	callerPackage: string,
): boolean {
	const { enablesEveryPackage, enabledPackages, excludedPackages } =
		resolveRelayEnabledPackages(configuredPackages);

	// Exclusions are checked first and against the caller directly, because they must also override
	// `set:EVERYTHING` — which enables packages that appear in no list, so there is nothing to filter
	// them out of.
	if (
		excludedPackages.some((excludedPackage) =>
			matchesRelayEnabledPackage(callerPackage, excludedPackage),
		)
	) {
		return false;
	}

	if (enablesEveryPackage) {
		return true;
	}

	return enabledPackages.some((enabledPackage) =>
		matchesRelayEnabledPackage(callerPackage, enabledPackage),
	);
}

function logRelayDecision({
	callerPackage,
	operationName,
	operationKey,
	phase,
	relayEnabled,
	decisionReason,
}: {
	callerPackage?: string;
	operationName?: string | null;
	operationKey?: string | null;
	phase: RelayDecisionPhase;
	relayEnabled: boolean;
	decisionReason: string;
}): void {
	if (!fg('confluence_relay_migration_diagnostics_enabled')) {
		return;
	}

	logRelayMigrationDiagnostic('Decision', {
		phase,
		callerPackage,
		operationName,
		operationKey,
		relayEnabled,
		decisionReason,
	});
}

export function isRelayEnabled(
	callerPackage?: string,
	options: RelayEnablementOptions = {},
): boolean {
	const phase = options.phase ?? 'unknown';
	const operationName = options.operationName ?? null;
	const operationKey =
		options.operationKey ?? getRelayOperationKey(operationName, options.variables) ?? null;

	// Query param override: ?disableRelay=1 or ?disableRelay=true forces Relay off
	if (isRelayDisabledByQueryParam()) {
		logRelayDecision({
			callerPackage,
			operationName,
			operationKey,
			phase,
			relayEnabled: false,
			decisionReason: 'disableRelay query param',
		});
		return false;
	}

	if (relayInteropEnabledOverride !== undefined) {
		logRelayDecision({
			callerPackage,
			operationName,
			operationKey,
			phase,
			relayEnabled: relayInteropEnabledOverride,
			decisionReason: 'relay interop override',
		});
		return relayInteropEnabledOverride;
	}

	if (process.env.BUILD_VARIANT === 'relay') {
		logRelayDecision({
			callerPackage,
			operationName,
			operationKey,
			phase,
			relayEnabled: true,
			decisionReason: 'BUILD_VARIANT relay',
		});
		return true;
	}

	if (!expVal('confluence_apollo_to_relay_migration', 'isEnabled', false)) {
		logRelayDecision({
			callerPackage,
			operationName,
			operationKey,
			phase,
			relayEnabled: false,
			decisionReason: 'master experiment disabled',
		});
		return false;
	}

	if (operationKey) {
		const operations = StatsigConfigurations.getStringList(
			'confluence_relay_enabled_operations',
			[],
		);
		if (operations.includes(operationKey)) {
			logRelayDecision({
				callerPackage,
				operationName,
				operationKey,
				phase,
				relayEnabled: true,
				decisionReason: 'operation allowlist',
			});
			return true;
		}

		logRelayDecision({
			callerPackage,
			operationName,
			operationKey,
			phase,
			relayEnabled: false,
			decisionReason:
				operations.length > 0 ? 'operation allowlist miss' : 'operation allowlist empty',
		});
	}

	if (!callerPackage) {
		logRelayDecision({
			callerPackage,
			operationName,
			operationKey,
			phase,
			relayEnabled: false,
			decisionReason: 'missing caller package',
		});
		return false;
	}

	if (!fg('confluence_relay_package_rollout_enabled')) {
		logRelayDecision({
			callerPackage,
			operationName,
			operationKey,
			phase,
			relayEnabled: false,
			decisionReason: 'package rollout feature gate disabled',
		});
		return false;
	}

	const relayEnabled = isPackageRelayEnabled(
		StatsigConfigurations.getStringList('confluence_relay_enabled_packages', []),
		callerPackage,
	);
	logRelayDecision({
		callerPackage,
		operationName,
		operationKey,
		phase,
		relayEnabled,
		decisionReason: 'package allowlist',
	});
	return relayEnabled;
}
